// Resolves the correct variant (image + link) for each predictive-search product card.
// Products can have 700+ variants, so variants are fetched via the Storefront GraphQL API
// (mirrors assets/product-card-grid.js) rather than iterating them in Liquid.
//   - If the product's `product.main_color` metafield is set, show that color's variant.
//   - Otherwise show the first suitable variant. Variants with `custom.active = false`
//     are hard-excluded and never shown.
// To avoid flashing the wrong image, the card image is held behind a skeleton until the
// correct variant image is preloaded, then faded in. Pagination stops as soon as a usable
// variant is found (usually one request), results are cached, and concurrent lookups for
// the same product are de-duplicated.
const PredictiveSearchMainColor = (function () {
  const STOREFRONT_ENDPOINT = ((window.LIVE && window.LIVE.storefrontUrl) || "https://live-ecom.myshopify.com/api/2025-07/graphql.json");
  const STOREFRONT_TOKEN = ((window.LIVE && window.LIVE.storefrontToken) || "ed761c0d0f68bc596e5e4dd4e29e6407");
  const IMAGE_WIDTH = 700;
  // Cache the RESOLVED variant per (product, main color) rather than raw variant lists,
  // and de-dup concurrent lookups so rapid typing never fires duplicate paginations.
  const resolutionCache = new Map(); // key `${gid}|${mainColorKey}` -> variant | null
  const inFlight = new Map();        // key -> Promise<variant | null>

  function normalizeColorToken(value) {
    return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function parseVariantIdFromGid(gid) {
    if (!gid) return '';
    const parts = String(gid).split('/');
    return parts[parts.length - 1] || '';
  }

  function sizedImageUrl(url, width) {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return url;
    }
  }

  function buildVariantUrl(baseHref, variantId) {
    if (!baseHref || !variantId) return baseHref || '';
    try {
      const normalized = new URL(baseHref, window.location.origin);
      normalized.searchParams.set('variant', variantId);
      if (/^https?:\/\//i.test(baseHref)) return normalized.toString();
      return normalized.pathname + normalized.search + normalized.hash;
    } catch (error) {
      const separator = baseHref.indexOf('?') === -1 ? '?' : '&';
      return baseHref + separator + 'variant=' + variantId;
    }
  }

  // custom.active is treated as visible unless it is explicitly false/0.
  function isVariantActive(variant) {
    const activeRaw = variant && variant.active && variant.active.value;
    if (activeRaw === null || activeRaw === undefined || activeRaw === '') return true;
    const normalized = String(activeRaw).trim().toLowerCase();
    if (normalized === 'false' || normalized === '0') return false;
    return normalized === 'true' || normalized === '1';
  }

  function getColorOptionValue(variant) {
    const opts = (variant && variant.selectedOptions) || [];
    const colorOpt = opts.find(function (option) {
      return option && option.name && option.value && option.name.toLowerCase().indexOf('color') !== -1;
    });
    return colorOpt ? colorOpt.value : '';
  }

  function variantMatchesMainColor(variant, mainColorKey) {
    if (!mainColorKey) return false;
    const optionColorKey = normalizeColorToken(getColorOptionValue(variant));
    if (optionColorKey && optionColorKey === mainColorKey) return true;
    const liveColorValue = variant && variant.liveColor && variant.liveColor.value;
    const liveColorKey = normalizeColorToken(liveColorValue || '');
    return !!(liveColorKey && liveColorKey.indexOf(mainColorKey) !== -1);
  }

  function pickVariantImageUrl(variant) {
    const edges = variant && variant.images && variant.images.references && variant.images.references.edges;
    if (Array.isArray(edges)) {
      for (let i = 0; i < edges.length; i++) {
        const node = edges[i] && edges[i].node;
        const url = (node && node.image && node.image.url) || (node && node.previewImage && node.previewImage.url);
        if (url) return url;
      }
    }
    return (variant && variant.image && variant.image.url) || '';
  }

  // Prefer an in-stock variant; otherwise the first in list order. Callers pass an
  // already active-filtered list, so "first" means the first ACTIVE variant.
  function bestByAvailability(list) {
    if (!list || !list.length) return null;
    let fallback = null;
    for (let i = 0; i < list.length; i++) {
      if (list[i] && list[i].availableForSale) return list[i];
      if (!fallback) fallback = list[i];
    }
    return fallback;
  }

  // custom.active is a HARD filter: an inactive variant must never be shown.
  function pickActiveColorMatch(variants, mainColorKey) {
    return bestByAvailability(variants.filter(function (variant) {
      return isVariantActive(variant) && variantMatchesMainColor(variant, mainColorKey);
    }));
  }

  function pickActive(variants) {
    return bestByAvailability(variants.filter(isVariantActive));
  }

  // Final choice once pagination is exhausted: an active main-color match if any,
  // else the first active variant, else null (leave the server-rendered default).
  function selectVariantFinal(variants, mainColorKey) {
    if (!variants || !variants.length) return null;
    const active = variants.filter(isVariantActive);
    if (!active.length) return null;
    if (mainColorKey) {
      const matches = active.filter(function (variant) {
        return variantMatchesMainColor(variant, mainColorKey);
      });
      if (matches.length) return bestByAvailability(matches);
    }
    return bestByAvailability(active);
  }

  const VARIANTS_QUERY = `
    query ($id: ID!, $cursor: String) {
      node(id: $id) {
        ... on Product {
          variants(first: 250, after: $cursor) {
            edges {
              node {
                id
                availableForSale
                selectedOptions { name value }
                image { url }
                active: metafield(namespace: "custom", key: "active") { value }
                liveColor: metafield(namespace: "live", key: "color") { value }
                images: metafield(namespace: "custom", key: "images") {
                  references(first: 5) {
                    edges {
                      node {
                        ... on MediaImage { image { url } }
                        ... on GenericFile { previewImage { url } }
                      }
                    }
                  }
                }
              }
            }
            pageInfo { endCursor hasNextPage }
          }
        }
      }
    }
  `;

  // Fetch a single page of variants. Returns { ok, variants, endCursor, hasNextPage }.
  // ok:false on any HTTP/network/GraphQL error (including a throttled HTTP-200 carrying
  // `errors`), so a failed page is never mistaken for "no more variants" or cached.
  async function fetchVariantsPage(gid, cursor) {
    let json;
    try {
      const res = await fetch(STOREFRONT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query: VARIANTS_QUERY, variables: { id: gid, cursor } })
      });
      if (!res.ok) return { ok: false };
      json = await res.json();
    } catch (networkError) {
      return { ok: false };
    }
    if (!json || json.errors) return { ok: false };
    const variants = json.data && json.data.node && json.data.node.variants;
    if (!variants) return { ok: false };
    const pageInfo = variants.pageInfo || {};
    return {
      ok: true,
      variants: (variants.edges || []).map(function (edge) { return edge.node; }),
      endCursor: pageInfo.endCursor || null,
      hasNextPage: !!pageInfo.hasNextPage
    };
  }

  // Paginate only as far as needed: stop as soon as a usable active variant is found
  // (a main-color match, or the first active variant when there is no main color).
  // Most products resolve on the first page, so this is usually a single request.
  async function paginateAndResolve(gid, mainColorKey, isCurrent) {
    const all = [];
    let cursor = null;
    let hasNext = true;
    let errored = false;

    while (hasNext) {
      if (typeof isCurrent === 'function' && !isCurrent()) {
        return { variant: null, cacheable: false };
      }
      const page = await fetchVariantsPage(gid, cursor);
      if (!page.ok) { errored = true; break; }
      for (let i = 0; i < page.variants.length; i++) all.push(page.variants[i]);

      const confident = mainColorKey ? pickActiveColorMatch(all, mainColorKey) : pickActive(all);
      if (confident) return { variant: confident, cacheable: true };

      cursor = page.endCursor;
      hasNext = page.hasNextPage && !!cursor;
    }

    // Only cache a fully-paginated result; a partial (errored) run must stay retryable.
    return { variant: selectVariantFinal(all, mainColorKey), cacheable: !errored };
  }

  function resolveVariant(gid, mainColorKey, isCurrent) {
    const key = gid + '|' + mainColorKey;
    if (resolutionCache.has(key)) return Promise.resolve(resolutionCache.get(key));
    if (inFlight.has(key)) return inFlight.get(key);

    const request = paginateAndResolve(gid, mainColorKey, isCurrent).then(function (result) {
      if (result.cacheable) resolutionCache.set(key, result.variant);
      return result.variant;
    });
    const tracked = request.finally(function () { inFlight.delete(key); });
    inFlight.set(key, tracked);
    return tracked;
  }

  function applyToCard(card, variant, container, done) {
    // Only rewrite the product links, never an anchor the wishlist widget injects.
    const variantId = parseVariantIdFromGid(variant.id);
    if (variantId) {
      const variantUrl = buildVariantUrl(card.getAttribute('data-product-url') || '', variantId);
      if (variantUrl) {
        card.querySelectorAll('.producto-top__link').forEach(function (anchor) {
          anchor.setAttribute('href', variantUrl);
        });
      }
    }

    const img = container ? container.querySelector('img') : null;
    const imageUrl = pickVariantImageUrl(variant);
    const targetSrc = imageUrl ? sizedImageUrl(imageUrl, IMAGE_WIDTH) : '';

    if (!img || !targetSrc || normalizeImageUrl(img.getAttribute('src')) === normalizeImageUrl(targetSrc)) {
      done();
      return;
    }

    // Preload the correct image, then swap + fade it in. The skeleton hides the old src
    // until now, so the wrong variant is never visible.
    const preload = new Image();
    preload.onload = function () {
      img.setAttribute('src', targetSrc);
      img.removeAttribute('srcset');
      done();
    };
    preload.onerror = done;
    preload.src = targetSrc;
  }

  function applyToResults(container, isCurrent) {
    if (!container) return;
    const cards = container.querySelectorAll('.producto-top[data-product-id]');
    cards.forEach(function (card) {
      const productId = (card.getAttribute('data-product-id') || '').replace(/\D+/g, '');
      if (!productId) return;
      if (card.hasAttribute('data-color-search-resolved')) return;

      const mainColorKey = normalizeColorToken(card.getAttribute('data-main-color'));
      const ssrActive = card.getAttribute('data-ssr-active') !== 'false';

      // No main color and the server already rendered an active variant: already correct.
      if (!mainColorKey && ssrActive) return;

      const imgContainer = card.querySelector('.color-image.item-product-img');
      if (imgContainer) imgContainer.classList.add('is-resolving');

      let settled = false;
      const done = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (imgContainer) imgContainer.classList.remove('is-resolving');
      };
      // Safety net: never leave the skeleton up if the network stalls.
      const timer = setTimeout(done, 4000);

      resolveVariant('gid://shopify/Product/' + productId, mainColorKey, isCurrent)
        .then(function (variant) {
          if (settled) return;
          if ((typeof isCurrent === 'function' && !isCurrent()) || !document.contains(card)) {
            done();
            return;
          }
          if (!variant) { done(); return; } // no active variant -> keep server default
          applyToCard(card, variant, imgContainer, done);
        })
        .catch(function () { done(); });
    });
  }

  return { applyToResults };
})();

class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.searchToken = 0;
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('#predictive-search');
      this.popularSearches = this.querySelector('#popular-searches');
      this.noResultsMessage = this.querySelector('#no-results-message');
      this.statusRegion = this.querySelector('.predictive-search__status');
      this.activeIndex = -1;

      if (!this.input.value.trim()) {
        this.showPopularSearches();
      }

      this.input.addEventListener('input', this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.input.addEventListener('keydown', this.onKeyDown.bind(this));

      // suggestions are product links, so they stay tab stops - keep the listbox
      // cursor on whichever one Tab lands on so both routes agree
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.addEventListener('focusin', (event) => {
          const option = event.target.closest('li[role="option"]');
          if (!option) return;
          const index = this.getOptions().indexOf(option);
          if (index >= 0) this.setActiveOption(index);
        });

        // the top-products grid is also inside this container, so test the listbox itself
        this.predictiveSearchResults.addEventListener('focusout', (event) => {
          const next = event.relatedTarget;
          if (next && next.closest && next.closest('ul[role="listbox"]')) return;
          this.setActiveOption(-1);
        });
      }
    }

    get isOpen() {
      return !!this.predictiveSearchResults && this.predictiveSearchResults.style.display === 'block';
    }

    getOptions() {
      if (!this.predictiveSearchResults) return [];
      return Array.from(
        this.predictiveSearchResults.querySelectorAll('ul[role="listbox"] li[role="option"]')
      );
    }

    prepareOptions() {
      const options = this.getOptions();
      options.forEach((option, index) => {
        option.id = `predictive-search-option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });
      this.setActiveOption(-1);
      this.announce(
        options.length
          ? `${options.length} suggestion${options.length === 1 ? '' : 's'} available.`
          : ''
      );
    }

    setActiveOption(index) {
      const options = this.getOptions();
      options.forEach((option) => option.setAttribute('aria-selected', 'false'));

      if (index < 0 || !options.length) {
        this.activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      const next = (index + options.length) % options.length;
      const option = options[next];
      option.setAttribute('aria-selected', 'true');
      this.activeIndex = next;
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    onKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (!this.isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.showPopularSearches();
        this.input.focus();
        return;
      }

      if (!this.isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActiveOption(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActiveOption(
            this.activeIndex <= 0 ? this.getOptions().length - 1 : this.activeIndex - 1
          );
          break;
        case 'Enter': {
          if (this.activeIndex < 0) return;
          const link = this.getOptions()[this.activeIndex].querySelector('a');
          if (!link) return;
          event.preventDefault();
          link.click();
          break;
        }
        default:
          break;
      }
    }

    announce(message) {
      if (this.statusRegion) this.statusRegion.textContent = message;
    }

    onChange() {
      const searchTerm = this.input.value.trim();

      if (!searchTerm.length) {
        this.close();
        this.hideNoResults();
        this.showPopularSearches();
        return;
      }

      this.hidePopularSearches();
      this.getSearchResults(searchTerm);
    }
  
    isColorCode(q) {
      if (!/^(?=.*[0-9])(?=.*[A-Za-z])[A-Za-z0-9]{3,10}$/.test(q)) return false;
      return (q.match(/[A-Za-z]/g) || []).length >= 2;
    }

    getSearchResults(searchTerm) {
      if(!this.predictiveSearchResults) return;
      const searchToken = ++this.searchToken;
      const queryTerm = /^\d{5}$/.test(searchTerm) ? ('1.' + searchTerm) : searchTerm;
      const allowBarcodePrefixFallback = queryTerm === searchTerm && this.isColorCode(searchTerm);
      this.fetchPredictiveResults(queryTerm, searchTerm, searchToken, allowBarcodePrefixFallback);
    }

    fetchPredictiveResults(queryTerm, searchTerm, searchToken, allowFallback) {
      // printed colours carry the code in the variant title, not in the SKU
      const fields = this.isColorCode(searchTerm)
        ? 'title,product_type,variants.sku,variants.title,tag'
        : 'title,product_type,variants.sku,tag';
      fetch(`/search/suggest?q=${encodeURIComponent(queryTerm)}&section_id=predictive-search&resources[type]=product&resources[options][fields]=${fields}`)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          if (searchToken !== this.searchToken) return;
          const parsed = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          if (!parsed) { this.close(); return; }
          const hasResults = parsed.querySelectorAll('li').length > 0;

          if (!hasResults && allowFallback) {
            this.fetchPredictiveResults('s.' + searchTerm.toLowerCase(), searchTerm, searchToken, false);
            return;
          }

          const resultsMarkup = parsed.innerHTML;
          this.predictiveSearchResults.innerHTML = resultsMarkup;
          if (!hasResults) {
            this.close();
            this.showNoResults(this.input.value.trim());
            this.showPopularSearches();
          } else {
            this.open();
            this.hideNoResults();
            this.hidePopularSearches();
            this.prepareOptions();
          }
          this.applyButtonHiding();
          this.initProductsSwiper();
          this.highlightSearchTerms(searchTerm);
          PredictiveSearchMainColor.applyToResults(
            this.predictiveSearchResults,
            () => searchToken === this.searchToken
          );
          if (typeof ReloadSmartWishlist !== "undefined" && $.isFunction(ReloadSmartWishlist)) {
            ReloadSmartWishlist();
          }
        })
        .catch((error) => {
          this.close();
          this.hideNoResults();
          throw error;
        });
    }
    highlightSearchTerms(searchTerm) {
      if (!this.predictiveSearchResults) return;
      const spans = this.predictiveSearchResults.querySelectorAll('ul[role="listbox"] li span');
      const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');

      spans.forEach(span => {
        const text = span.textContent;
        if (regex.test(text)) {
          regex.lastIndex = 0;
          span.innerHTML = text.replace(regex, '<span class="search-highlight">$1</span>');
        }
      });
    }

    initProductsSwiper() {
      if (!this.predictiveSearchResults) return;
      const swiperEl = this.predictiveSearchResults.querySelector('.top-suggestions__products');
      if (!swiperEl) return;
      if (swiperEl.swiper) swiperEl.swiper.destroy(true, true);
      new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        freeMode: true,
        grabCursor: true,
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          768: {
            slidesPerView: 'auto',
            spaceBetween: 16,
          },
          1800: {
            slidesPerView: 4,
            spaceBetween: 16,
          }
        }
      });
    }

    applyButtonHiding() {
      if(!this.predictiveSearchResults) return;
      const topDivs = this.predictiveSearchResults.querySelectorAll('.color-top-suggestions');

      topDivs.forEach(topDiv => {
        const buttons = topDiv.querySelectorAll('button.swatch');
        if (buttons.length > 2) {
          buttons.forEach((button, index) => {
            if (index >= 2) {
              button.classList.add('hidden');
            }
          });
        }
      });
    }
    showPopularSearches() {
      if (this.popularSearches) this.popularSearches.style.display = 'block';
    }

    hidePopularSearches() {
      if (this.popularSearches) this.popularSearches.style.display = 'none';
    }

    open() {
      if(!this.predictiveSearchResults) return;
      this.predictiveSearchResults.style.display = 'block';
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if(!this.predictiveSearchResults) return;
      this.predictiveSearchResults.style.display = 'none';
      this.input.setAttribute('aria-expanded', 'false');
      this.setActiveOption(-1);
    }
  
    showNoResults(term) {
      if (this.noResultsMessage) {
        this.noResultsMessage.textContent = `No results found for "${term}"`;
        this.noResultsMessage.style.display = 'block';
      }
      this.announce(`No results found for ${term}`);
    }

    hideNoResults() {
      if (this.noResultsMessage) this.noResultsMessage.style.display = 'none';
      this.announce('');
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
  